/**
 * Escritor de Gramps XML 1.7.2.
 *
 * Dos reglas que no se negocian:
 *
 *  1. El orden de los elementos sale de las listas de `orden.ts`, que son
 *     copia literal del DTD. Si se escribe en otro orden, el fichero no
 *     valida y Gramps lo rechaza.
 *  2. Todo lo que `parse.ts` guardo en bruto se devuelve en su posicion del
 *     DTD. Lo que entro, sale.
 */
import {
  type Apellido,
  type Arbol,
  type Evento,
  type Familia,
  type Fecha,
  type Lugar,
  type Nombre,
  type NombreLugar,
  type Otros,
  type Persona,
  type RefEvento,
  type RefHijo,
} from "../model.js";
import {
  FECHA,
  ORDEN_CHILDREF,
  ORDEN_DATABASE,
  ORDEN_EVENT,
  ORDEN_EVENTREF,
  ORDEN_FAMILY,
  ORDEN_NAME,
  ORDEN_PERSON,
  ORDEN_PLACEOBJ,
  SECCIONES_MODELADAS,
} from "./orden.js";

// ---------------------------------------------------------------------------
// Escape
// ---------------------------------------------------------------------------

function esc(t: string): string {
  return t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escAtrib(t: string): string {
  return esc(t).replace(/"/g, "&quot;").replace(/\r?\n/g, "&#10;");
}

/** Atributos en orden estable, omitiendo los vacios/indefinidos. */
function attrs(pares: Array<[string, string | undefined]>): string {
  return pares
    .filter((p): p is [string, string] => p[1] !== undefined && p[1] !== "")
    .map(([k, v]) => ` ${k}="${escAtrib(v)}"`)
    .join("");
}

function extras(o: Record<string, string>): Array<[string, string]> {
  return Object.keys(o)
    .sort()
    .map((k) => [k, o[k] as string]);
}

// ---------------------------------------------------------------------------
// Fragmentos en bruto
// ---------------------------------------------------------------------------

/**
 * Limpia un fragmento conservado en bruto.
 *
 * El serializador del DOM anade `xmlns="..."` a los fragmentos que hereda el
 * espacio de nombres del documento. Dentro de <database> esa declaracion es
 * redundante, y ademas el DTD solo declara el atributo `xmlns` en <database>:
 * dejarla haria que el fichero NO validase. Hay que quitarla.
 */
function limpiarCrudo(xml: string): string {
  return xml.replace(
    /\s+xmlns="http:\/\/gramps-project\.org\/xml\/[^"]*"/g,
    "",
  );
}

/** Escribe los fragmentos en bruto de una etiqueta, con sangria. */
function crudos(o: Otros, etiqueta: string, sangria: string): string[] {
  const xs = o[etiqueta];
  if (!xs?.length) return [];
  return xs.map((x) => sangria + limpiarCrudo(x));
}

// ---------------------------------------------------------------------------
// Piezas pequenas
// ---------------------------------------------------------------------------

function escFecha(f: Fecha | undefined, sangria: string): string[] {
  if (!f) return [];
  const pares = Object.keys(f.atribs)
    .sort()
    .map((k) => [k, f.atribs[k]] as [string, string | undefined]);
  return [`${sangria}<${f.etiqueta}${attrs(pares)}/>`];
}

function escApellido(a: Apellido, sangria: string): string {
  const pares: Array<[string, string | undefined]> = [
    ["prefix", a.prefix],
    ["prim", a.prim ? "1" : undefined],
    ["derivation", a.derivation],
    ["connector", a.connector],
    ...extras(a.otrosAtribs),
  ];
  return `${sangria}<surname${attrs(pares)}>${esc(a.texto)}</surname>`;
}

function escNombre(n: Nombre, sangria: string): string[] {
  const s = sangria + "  ";
  const out: string[] = [];
  const pares = Object.keys(n.atribs)
    .sort()
    .map((k) => [k, n.atribs[k]] as [string, string | undefined]);
  out.push(`${sangria}<name${attrs(pares)}>`);

  const texto = (et: string, v: string | undefined): void => {
    if (v !== undefined && v !== "") out.push(`${s}<${et}>${esc(v)}</${et}>`);
  };

  for (const et of ORDEN_NAME) {
    switch (et) {
      case "first": texto("first", n.first); break;
      case "call": texto("call", n.call); break;
      case "surname":
        for (const a of n.apellidos) out.push(escApellido(a, s));
        break;
      case "suffix": texto("suffix", n.suffix); break;
      case "title": texto("title", n.title); break;
      case "nick": texto("nick", n.nick); break;
      case "familynick": texto("familynick", n.familynick); break;
      case "group": texto("group", n.group); break;
      case FECHA: out.push(...escFecha(n.fecha, s)); break;
      default: out.push(...crudos(n.otros, et, s));
    }
  }
  out.push(`${sangria}</name>`);
  return out;
}

/** Un elemento con hijos en bruto: se cierra solo si no tiene ninguno. */
function escRef(
  etiqueta: string,
  pares: Array<[string, string | undefined]>,
  otros: Otros,
  orden: readonly string[],
  sangria: string,
): string[] {
  const dentro: string[] = [];
  for (const et of orden) dentro.push(...crudos(otros, et, sangria + "  "));
  if (!dentro.length) return [`${sangria}<${etiqueta}${attrs(pares)}/>`];
  return [
    `${sangria}<${etiqueta}${attrs(pares)}>`,
    ...dentro,
    `${sangria}</${etiqueta}>`,
  ];
}

function escRefEvento(r: RefEvento, sangria: string): string[] {
  return escRef(
    "eventref",
    [["hlink", r.hlink], ["role", r.role], ...extras(r.otrosAtribs)],
    r.otros,
    ORDEN_EVENTREF,
    sangria,
  );
}

function escRefHijo(r: RefHijo, sangria: string): string[] {
  return escRef(
    "childref",
    [
      ["hlink", r.hlink],
      ["mrel", r.mrel],
      ["frel", r.frel],
      ...extras(r.otrosAtribs),
    ],
    r.otros,
    ORDEN_CHILDREF,
    sangria,
  );
}

// ---------------------------------------------------------------------------
// Objetos de primer nivel
// ---------------------------------------------------------------------------

function escPersona(p: Persona, sangria: string): string[] {
  const s = sangria + "  ";
  const out: string[] = [
    `${sangria}<person${attrs([
      ["handle", p.handle],
      ["change", p.change],
      ["id", p.id],
      ["priv", p.priv],
      ...extras(p.otrosAtribs),
    ])}>`,
  ];

  for (const et of ORDEN_PERSON) {
    switch (et) {
      case "gender":
        out.push(`${s}<gender>${p.sexo}</gender>`);
        break;
      case "name":
        for (const n of p.nombres) out.push(...escNombre(n, s));
        break;
      case "eventref":
        for (const r of p.eventos) out.push(...escRefEvento(r, s));
        break;
      case "childof":
        for (const h of p.childof) out.push(`${s}<childof hlink="${escAtrib(h)}"/>`);
        break;
      case "parentin":
        for (const h of p.parentin) out.push(`${s}<parentin hlink="${escAtrib(h)}"/>`);
        break;
      default:
        out.push(...crudos(p.otros, et, s));
    }
  }
  out.push(`${sangria}</person>`);
  return out;
}

function escFamilia(f: Familia, sangria: string): string[] {
  const s = sangria + "  ";
  const out: string[] = [
    `${sangria}<family${attrs([
      ["handle", f.handle],
      ["change", f.change],
      ["id", f.id],
      ["priv", f.priv],
      ...extras(f.otrosAtribs),
    ])}>`,
  ];

  for (const et of ORDEN_FAMILY) {
    switch (et) {
      case "rel":
        if (f.rel !== undefined) out.push(`${s}<rel type="${escAtrib(f.rel)}"/>`);
        break;
      case "father":
        if (f.padre) out.push(`${s}<father hlink="${escAtrib(f.padre)}"/>`);
        break;
      case "mother":
        if (f.madre) out.push(`${s}<mother hlink="${escAtrib(f.madre)}"/>`);
        break;
      case "eventref":
        for (const r of f.eventos) out.push(...escRefEvento(r, s));
        break;
      case "childref":
        for (const r of f.hijos) out.push(...escRefHijo(r, s));
        break;
      default:
        out.push(...crudos(f.otros, et, s));
    }
  }
  out.push(`${sangria}</family>`);
  return out;
}

function escEvento(e: Evento, sangria: string): string[] {
  const s = sangria + "  ";
  const out: string[] = [
    `${sangria}<event${attrs([
      ["handle", e.handle],
      ["change", e.change],
      ["id", e.id],
      ["priv", e.priv],
      ...extras(e.otrosAtribs),
    ])}>`,
  ];

  for (const et of ORDEN_EVENT) {
    switch (et) {
      case "type":
        if (e.tipo !== undefined) out.push(`${s}<type>${esc(e.tipo)}</type>`);
        break;
      case FECHA:
        out.push(...escFecha(e.fecha, s));
        break;
      case "place":
        if (e.lugar) out.push(`${s}<place hlink="${escAtrib(e.lugar)}"/>`);
        break;
      case "description":
        if (e.descripcion !== undefined && e.descripcion !== "") {
          out.push(`${s}<description>${esc(e.descripcion)}</description>`);
        }
        break;
      default:
        out.push(...crudos(e.otros, et, s));
    }
  }
  out.push(`${sangria}</event>`);
  return out;
}

function escNombreLugar(n: NombreLugar, sangria: string): string[] {
  const pares: Array<[string, string | undefined]> = [
    ["value", n.value],
    ["lang", n.lang],
    ...extras(n.otrosAtribs),
  ];
  if (!n.fecha) return [`${sangria}<pname${attrs(pares)}/>`];
  return [
    `${sangria}<pname${attrs(pares)}>`,
    ...escFecha(n.fecha, sangria + "  "),
    `${sangria}</pname>`,
  ];
}

function escLugar(l: Lugar, sangria: string): string[] {
  const s = sangria + "  ";
  const out: string[] = [
    `${sangria}<placeobj${attrs([
      ["handle", l.handle],
      ["change", l.change],
      ["id", l.id],
      ["priv", l.priv],
      ["type", l.tipo],
      ...extras(l.otrosAtribs),
    ])}>`,
  ];

  for (const et of ORDEN_PLACEOBJ) {
    switch (et) {
      case "ptitle":
        if (l.titulo) out.push(`${s}<ptitle>${esc(l.titulo)}</ptitle>`);
        break;
      case "code":
        if (l.codigo) out.push(`${s}<code>${esc(l.codigo)}</code>`);
        break;
      case "pname":
        for (const n of l.nombres) out.push(...escNombreLugar(n, s));
        break;
      default:
        out.push(...crudos(l.otros, et, s));
    }
  }
  out.push(`${sangria}</placeobj>`);
  return out;
}

// ---------------------------------------------------------------------------
// Documento completo
// ---------------------------------------------------------------------------

export interface OpcionesEscritura {
  /** Version del formato a declarar. Por omision, la del arbol. */
  version?: string;
  /** Fecha de <created>. Por omision, la que traia el arbol. */
  fechaCreado?: string;
}

/**
 * Convierte el arbol a texto Gramps XML.
 *
 * El resultado valida contra grampsxml.dtd y Gramps 6.0 lo abre. Las
 * secciones y los elementos que no modelamos vuelven en su sitio.
 */
export function escribirGramps(arbol: Arbol, op: OpcionesEscritura = {}): string {
  const v = op.version ?? arbol.version ?? "1.7.2";
  const out: string[] = [];
  out.push('<?xml version="1.0" encoding="UTF-8"?>');
  out.push(
    `<!DOCTYPE database PUBLIC "-//Gramps//DTD Gramps XML ${v}//EN"` +
      ` "http://gramps-project.org/xml/${v}/grampsxml.dtd">`,
  );
  out.push(`<database xmlns="http://gramps-project.org/xml/${v}/">`);

  for (const sec of ORDEN_DATABASE) {
    if (sec === "header") {
      out.push("  <header>");
      out.push(
        `    <created${attrs([
          ["date", op.fechaCreado ?? arbol.cabecera.creadoFecha],
          ["version", arbol.cabecera.creadoVersion],
        ])}/>`,
      );
      // researcher y mediapath tal cual venian
      for (const et of ["researcher", "mediapath"]) {
        out.push(...crudos(arbol.cabecera.otros, et, "    "));
      }
      if (!arbol.cabecera.otros["researcher"]) out.push("    <researcher/>");
      out.push("  </header>");
      continue;
    }

    if (!(SECCIONES_MODELADAS as readonly string[]).includes(sec)) {
      const crudo = arbol.seccionesCrudas[sec];
      if (crudo) out.push("  " + limpiarCrudo(crudo));
      continue;
    }

    switch (sec) {
      case "events":
        if (arbol.eventos.size) {
          out.push("  <events>");
          for (const e of arbol.eventos.values()) out.push(...escEvento(e, "    "));
          out.push("  </events>");
        }
        break;
      case "people":
        if (arbol.personas.size) {
          const pares = Object.keys(arbol.atribsPeople)
            .sort()
            .map((k) => [k, arbol.atribsPeople[k]] as [string, string | undefined]);
          out.push(`  <people${attrs(pares)}>`);
          for (const p of arbol.personas.values()) out.push(...escPersona(p, "    "));
          out.push("  </people>");
        }
        break;
      case "families":
        if (arbol.familias.size) {
          out.push("  <families>");
          for (const f of arbol.familias.values()) out.push(...escFamilia(f, "    "));
          out.push("  </families>");
        }
        break;
      case "places":
        if (arbol.lugares.size) {
          out.push("  <places>");
          for (const l of arbol.lugares.values()) out.push(...escLugar(l, "    "));
          out.push("  </places>");
        }
        break;
    }
  }

  out.push("</database>");
  return out.join("\n") + "\n";
}
